import { useTranslation } from 'react-i18next'
import { Sun, SunMoon, Moon } from 'lucide-react'
import type { V2Mode } from '@/config/designV2'

interface DisplayModeSwitcherProps {
  currentMode: V2Mode
  onChange: (mode: V2Mode) => void
}

const modes: { value: V2Mode; icon: typeof Sun; labelKey: string; labelFallback: string }[] = [
  { value: 'light', icon: Sun, labelKey: 'layout.modeLight', labelFallback: 'Light' },
  { value: 'dim', icon: SunMoon, labelKey: 'layout.modeDim', labelFallback: 'Dim' },
  { value: 'dark', icon: Moon, labelKey: 'layout.modeDark', labelFallback: 'Dark' },
]

export default function DisplayModeSwitcher({ currentMode, onChange }: DisplayModeSwitcherProps) {
  const { t } = useTranslation()

  return (
    <div className="flex items-center gap-1 rounded-lg bg-[var(--v2-surface-inset)] p-1">
      {modes.map(({ value, icon: Icon, labelKey, labelFallback }) => {
        const isActive = currentMode === value
        const label = t(labelKey, labelFallback)
        return (
          <button
            data-testid="mode-switcher-change-btn"
            key={value}
            type="button"
            onClick={() => onChange(value)}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 min-h-[36px] transition-all duration-200 ${
              isActive
                ? 'bg-[var(--v2-surface-raised)] text-[var(--v2-text)] shadow-sm'
                : 'text-[var(--v2-text-tertiary)] hover:text-[var(--v2-text-secondary)]'
            }`}
            aria-label={label}
            aria-pressed={isActive}
          >
            <Icon size={14} />
            <span className="text-xs font-medium">{label}</span>
          </button>
        )
      })}
    </div>
  )
}
